import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Clock, Hash, Package, Store, Truck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { api } from "@/lib/api";
import { audFull, fmtDate } from "@/lib/format";
import { slotMeta } from "@/lib/deliverySlots";

/**
 * Phase 133 — Dashboard "Deliveries this week": every supplier delivery landing Mon–Sun across the jobs you can see,
 * one column per day, coloured by the supplier's confirmed time slot. Click a delivery → that job's project schedule.
 */

const shiftWeek = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n * 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const rangeLabel = (start, end) => {
  const a = new Date(start + "T00:00:00");
  const b = new Date(end + "T00:00:00");
  const sameMonth = a.getMonth() === b.getMonth();
  const left = a.toLocaleDateString("en-AU", { day: "numeric", month: sameMonth ? undefined : "short" });
  const right = b.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  return `${left} – ${right}`;
};

const DeliveryPill = ({ r, onOpen }) => {
  const slot = slotMeta(r.time_slot);
  const delivered = Boolean(r.delivered_at);
  const problem = Boolean(r.delivery_problem);
  const awaiting = !r.confirmed && !delivered;
  const color = slot?.color;
  return (
    <HoverCard openDelay={150} closeDelay={60}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={onOpen}
          className={`group flex w-full flex-col gap-0.5 rounded-md border px-2 py-1.5 text-left text-[11px] leading-tight transition-colors duration-150 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${awaiting ? "border-dashed border-border" : "border-border/70"} ${delivered ? "opacity-70" : ""}`}
          style={!awaiting && color ? { borderLeftColor: color, borderLeftWidth: 3 } : undefined}
          data-testid={`dash-delivery-${r.id}`}
          data-slot={r.time_slot || ""}
        >
          <span className="flex items-center gap-1.5">
            <Truck className="h-3 w-3 shrink-0" style={{ color: color || "hsl(var(--muted-foreground))" }} aria-hidden="true" />
            <span className="truncate font-semibold text-foreground">{r.supplier_name || "Supplier"}</span>
            {delivered ? <CheckCircle2 className="ml-auto h-3 w-3 shrink-0 text-emerald-400" aria-label="Delivered" /> : problem ? <AlertTriangle className="ml-auto h-3 w-3 shrink-0 text-red-400" aria-label="Not delivered" /> : null}
          </span>
          <span className="truncate text-muted-foreground">{r.product}</span>
          <span className="flex items-center justify-between gap-1 text-[10px]">
            <span className="truncate text-muted-foreground/80">{r.job_name}</span>
            {slot ? (
              <span className="shrink-0 font-semibold" style={{ color }}>{slot.window}</span>
            ) : (
              <span className="shrink-0 italic text-muted-foreground/70">{delivered ? "delivered" : "awaiting"}</span>
            )}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="z-[300] w-80 border-border/80 bg-popover/95 p-3 text-xs shadow-lg backdrop-blur-md">
        <p className="font-semibold leading-snug" data-testid={`dash-delivery-hover-${r.id}`}>{r.product}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {delivered ? `Delivered${r.actual_delivery_date ? ` · ${fmtDate(r.actual_delivery_date)}` : ""}` : problem ? "Flagged not delivered — supplier asked for a new date" : r.confirmed ? `Supplier confirmed${r.confirmed_by ? ` by ${r.confirmed_by}` : ""}` : "Awaiting the supplier's confirmation"}
        </p>
        <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1">
          <dt className="flex items-center gap-1 text-muted-foreground"><Store className="h-3 w-3" aria-hidden="true" /> Supplier</dt>
          <dd className="truncate font-medium">{r.supplier_name || "—"}</dd>
          <dt className="flex items-center gap-1 text-muted-foreground"><Hash className="h-3 w-3" aria-hidden="true" /> PO ref</dt>
          <dd className="font-mono-nums font-medium">{r.po_number} <span className="font-sans text-muted-foreground">· {audFull(r.total_inc_gst)} inc GST</span></dd>
          <dt className="flex items-center gap-1 text-muted-foreground"><Clock className="h-3 w-3" aria-hidden="true" /> Arriving</dt>
          <dd>
            <span className="font-medium">{fmtDate(r.expected_date)}</span>
            {slot ? (
              <span className="ml-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-semibold" style={{ borderColor: `${slot.color}66`, color: slot.color, backgroundColor: `${slot.color}1a` }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: slot.color }} aria-hidden="true" /> {slot.label} · {slot.window}
              </span>
            ) : null}
          </dd>
          <dt className="flex items-center gap-1 text-muted-foreground"><Package className="h-3 w-3" aria-hidden="true" /> Job</dt>
          <dd className="truncate">{r.job_name}</dd>
        </dl>
        <p className="mt-2 text-[10px] text-muted-foreground">Click to open the project schedule</p>
      </HoverCardContent>
    </HoverCard>
  );
};

export const DeliveriesWeekStrip = ({ className = "" }) => {
  const navigate = useNavigate();
  const [start, setStart] = useState("");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/deliveries/week", { params: start ? { start } : {} })
      .then((res) => {
        setData(res.data);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [start]);

  useEffect(load, [load]);

  const isThisWeek = !start || (data && data.today >= data.start && data.today <= data.end);

  return (
    <Card className={`min-w-0 shadow-sm ${className}`} data-testid="dashboard-deliveries-week-card">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Truck className="h-4 w-4 text-primary" aria-hidden="true" /> {isThisWeek ? "Deliveries this week" : "Deliveries"}
          {data && (
            <span className="text-sm font-normal text-muted-foreground" data-testid="dashboard-deliveries-week-range">{rangeLabel(data.start, data.end)}</span>
          )}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {data && data.total > 0 && (
            <div className="hidden items-center gap-1.5 sm:flex" data-testid="dashboard-deliveries-week-counts">
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 font-normal text-emerald-300">{data.confirmed} confirmed</Badge>
              {data.awaiting > 0 && <Badge variant="outline" className="border-border font-normal text-muted-foreground">{data.awaiting} awaiting supplier</Badge>}
              {data.delivered > 0 && <Badge variant="outline" className="border-border font-normal text-muted-foreground">{data.delivered} delivered</Badge>}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" aria-label="Previous week" onClick={() => setStart(shiftWeek(data?.start || new Date().toISOString().slice(0, 10), -1))} data-testid="dashboard-deliveries-week-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setStart("")} disabled={isThisWeek} data-testid="dashboard-deliveries-week-today">
              This week
            </Button>
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" aria-label="Next week" onClick={() => setStart(shiftWeek(data?.start || new Date().toISOString().slice(0, 10), 1))} data-testid="dashboard-deliveries-week-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {failed ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed px-3 py-4 text-sm" data-testid="dashboard-deliveries-week-error">
            <span className="text-muted-foreground">Couldn&apos;t load this week&apos;s deliveries.</span>
            <Button size="sm" variant="secondary" onClick={load} data-testid="dashboard-deliveries-week-retry">Try again</Button>
          </div>
        ) : loading && !data ? (
          <div className="grid grid-cols-7 gap-2" data-testid="dashboard-deliveries-week-loading">
            {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
          </div>
        ) : data && data.total === 0 ? (
          <div className="flex items-center gap-3 rounded-lg border border-dashed px-4 py-5 text-sm text-muted-foreground" data-testid="dashboard-deliveries-week-empty">
            <Truck className="h-5 w-5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
            <span>No supplier deliveries booked {isThisWeek ? "this week" : "that week"}. Accept a supplier quote and the purchase order, delivery date and time slot land here automatically.</span>
          </div>
        ) : data ? (
          <div className={`grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7 ${loading ? "opacity-60" : ""}`} data-testid="dashboard-deliveries-week-days">
            {data.days.map((day) => (
              <div
                key={day.date}
                className={`min-w-0 rounded-lg border px-1.5 pb-1.5 pt-1 ${day.is_today ? "border-primary/40 bg-primary/[0.06]" : day.is_past ? "border-border/50 bg-muted/20" : "border-border/70"}`}
                data-testid={`dashboard-deliveries-day-${day.date}`}
              >
                <p className={`mb-1 flex items-baseline justify-between px-0.5 text-[10px] font-semibold uppercase tracking-wide ${day.is_today ? "text-primary" : "text-muted-foreground"}`}>
                  <span>{day.is_today ? "Today" : day.label}</span>
                  <span className="tabular-nums">{day.day}</span>
                </p>
                {day.deliveries.length === 0 ? (
                  <p className="px-0.5 py-2 text-center text-[10px] text-muted-foreground/50">—</p>
                ) : (
                  <div className="space-y-1">
                    {day.deliveries.map((r) => (
                      <DeliveryPill key={r.id} r={r} onOpen={() => navigate(`/jobs/${r.job_id}?tab=gantt`)} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
